import { ARENA, COVER } from '../../config/constants.js';
import { CoverPillar } from '../entities/CoverPillar.js';

/**
 * Owns the Resonant Pillars (destructible cover) for boss fights: a mirrored
 * pair spawns when the fight starts (Game._startBossStage calls
 * beginBossFight), one more joins on each boss phase transition, capped at
 * COVER.maxActive. Fresh set per boss, no regen — camping behind one drains
 * it and there's no getting it back. Wave stages get none: waves are about
 * movement, boss fights are about geometry.
 *
 * The actual bullet-eating lives in Game._blockBossBullets (the one place
 * that can see both bullet pools); this system just tracks pillars and emits
 * pillar:spawned / pillar:cracked / pillar:shattered for feedback.
 */
export class CoverSystem {
  constructor(eventBus, { getPlayer, getBoss } = {}) {
    this.eventBus = eventBus;
    this._getPlayer = getPlayer;
    this._getBoss = getBoss;
    this.pillars = [];
    this._active = false;
    this._hpMultiplier = 1;

    eventBus.on('boss:phase-change', () => {
      if (this._active) this._spawnPillar();
    });
    // Heralds fight without pillars (beginBossFight is never called for
    // them), and both clears below double as safety nets for stage churn.
    eventBus.on('boss:died', () => this.clear());
    eventBus.on('wave:started', () => this.clear());
  }

  /** hpMultiplier: endless per-loop scaling (Game._endlessLoopMultiplier) so cover stays relevant at depth. */
  beginBossFight({ hpMultiplier = 1 } = {}) {
    this.clear();
    this._active = true;
    this._hpMultiplier = hpMultiplier;
    for (const xFraction of COVER.spawnXFractions) this._spawnPillar(xFraction);
  }

  clear() {
    this.pillars = [];
    this._active = false;
  }

  update(dt) {
    for (const pillar of this.pillars) {
      pillar.hitFlashTimer = Math.max(0, pillar.hitFlashTimer - dt);
    }
  }

  /**
   * Routes a blocked bullet's damage into the pillar and turns the HP
   * thresholds into events — kept here (not in CoverPillar) so the entity
   * stays event-bus-free like HealthPod.
   */
  damagePillar(pillar, damage) {
    const stageBefore = pillar.crackStage;
    pillar.takeBulletHit(damage);
    if (!pillar.alive) {
      this.pillars = this.pillars.filter((p) => p !== pillar);
      this.eventBus.emit('pillar:shattered', { x: pillar.x, y: pillar.y });
    } else if (pillar.crackStage !== stageBefore) {
      this.eventBus.emit('pillar:cracked', { x: pillar.x, y: pillar.y, stage: pillar.crackStage });
    }
  }

  forEachAlive(fn) {
    for (const pillar of this.pillars) {
      if (pillar.alive) fn(pillar);
    }
  }

  _spawnPillar(xFraction = null) {
    if (this.pillars.length >= COVER.maxActive) return;
    const player = this._getPlayer?.();
    const boss = this._getBoss?.();
    // A few placement tries against the distance constraints, rather than
    // exact rejection sampling — same approach as PickupSystem._trySpawn.
    for (let attempt = 0; attempt < 10; attempt++) {
      const fx = xFraction ?? (0.25 + Math.random() * 0.5);
      const x = ARENA.x + ARENA.width * fx + (Math.random() * 2 - 1) * COVER.placementJitter;
      const y = ARENA.y + ARENA.height * 0.5 + (Math.random() * 2 - 1) * COVER.placementJitter * 2;
      const farFromPlayer = !player || Math.hypot(x - player.x, y - player.y) >= COVER.minDistPlayer;
      const farFromBoss = !boss || Math.hypot(x - boss.x, y - boss.y) >= COVER.minDistBoss;
      if (farFromPlayer && farFromBoss) {
        this.pillars.push(new CoverPillar({ x, y, hp: COVER.hp * this._hpMultiplier }));
        this.eventBus.emit('pillar:spawned', { x, y });
        return;
      }
    }
  }
}
